import { useEffect, useState } from "react";
import { api, gbp, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Archive, Upload, Loader2, Tags, X, Clock, AlertTriangle, LineChart as LineIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BLANK = { name: "", description: "", image_url: "", price_incl_vat: 0, vat_rate: 20, components: [], active: true, schedule_enabled: false, days: [], start_time: "12:00", end_time: "14:00", happy_hour_enabled: false, happy_hour_discount_percent: 20, happy_hour_days: [], happy_hour_start: "15:00", happy_hour_end: "17:00", ab_enabled: false, variant_b: { name: "", price_incl_vat: 0 }, scope: "all", building_ids: [], group: "" };
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function scheduleSummary(c) {
  if (!c.schedule_enabled) return "Always available";
  const days = (c.days || []).length ? (c.days || []).slice().sort().map((i) => DAYS[i]).join(", ") : "Every day";
  const time = c.start_time && c.end_time ? `${c.start_time}–${c.end_time}` : "";
  return `${days} ${time}`.trim();
}

export default function Combos() {
  const [combos, setCombos] = useState([]);
  const [products, setProducts] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pick, setPick] = useState("");
  const [timeline, setTimeline] = useState(null);

  const load = () => api.get("/combos").then((r) => setCombos(r.data));
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data.filter((p) => !p.archived)));
    api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => {});
  }, []);
  const groups = [...new Set(buildings.map((b) => b.group).filter(Boolean))];
  const toggleBuilding = (id) => upd("building_ids", (form.building_ids || []).includes(id) ? form.building_ids.filter((x) => x !== id) : [...(form.building_ids || []), id]);

  const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const openTimeline = async (c, weekday) => {
    const wd = weekday ?? (new Date().getDay() + 6) % 7;
    const res = await api.get(`/combos/${c.id}/timeline?weekday=${wd}`);
    setTimeline({ combo: c, weekday: wd, ...res.data });
  };

  // Live below-cost guardrail for the deal being edited
  const formCost = form.components.reduce((s, comp) => {
    const p = products.find((x) => x.id === comp.product_id);
    return s + (p ? Number(p.cost_price || 0) * Number(comp.quantity || 1) : 0);
  }, 0);
  const hhPriceA = Number(form.price_incl_vat) * (1 - Number(form.happy_hour_discount_percent) / 100);
  const hhPriceB = Number(form.variant_b?.price_incl_vat || 0) * (1 - Number(form.happy_hour_discount_percent) / 100);
  const belowCostA = form.happy_hour_enabled && hhPriceA < formCost;
  const belowCostB = form.happy_hour_enabled && form.ab_enabled && hhPriceB < formCost;

  const nameFor = (id) => products.find((p) => p.id === id)?.name || id;
  const openNew = () => { setForm(BLANK); setEditId(null); setPick(""); setOpen(true); };
  const openEdit = (c) => { setForm({ ...BLANK, ...c }); setEditId(c.id); setPick(""); setOpen(true); };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addComponent = () => {
    if (!pick) return;
    if (form.components.find((c) => c.product_id === pick)) return;
    upd("components", [...form.components, { product_id: pick, quantity: 1 }]);
    setPick("");
  };
  const setQty = (id, qty) => upd("components", form.components.map((c) => (c.product_id === id ? { ...c, quantity: Number(qty) } : c)));
  const removeComponent = (id) => upd("components", form.components.filter((c) => c.product_id !== id));
  const toggleDay = (i) => upd("days", form.days.includes(i) ? form.days.filter((d) => d !== i) : [...form.days, i]);
  const toggleHHDay = (i) => upd("happy_hour_days", form.happy_hour_days.includes(i) ? form.happy_hour_days.filter((d) => d !== i) : [...form.happy_hour_days, i]);

  const save = async () => {
    if (!form.components.length) { toast.error("Add at least one product to the deal"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_incl_vat: Number(form.price_incl_vat),
        vat_rate: Number(form.vat_rate),
        happy_hour_discount_percent: Number(form.happy_hour_discount_percent),
        variant_b: form.ab_enabled ? { name: form.variant_b?.name || "", price_incl_vat: Number(form.variant_b?.price_incl_vat || 0) } : null,
      };
      if (editId) await api.put(`/combos/${editId}`, payload);
      else await api.post("/combos", payload);
      toast.success(editId ? "Deal updated" : "Deal created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const archive = async (c) => { await api.post(`/combos/${c.id}/archive`); toast.success("Deal archived"); load(); };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("image_url", `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Meal Deals</h1>
          <p className="text-slate-500 mt-1">Bundle products together to lift basket size</p>
        </div>
        <Button data-testid="combo-new-button" onClick={openNew} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1.5" /> New deal</Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {combos.filter((c) => !c.archived).map((c) => (
          <div key={c.id} data-testid={`combo-card-${c.id}`} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden">
            <div className="h-36 bg-slate-100 dark:bg-slate-800 overflow-hidden relative">
              {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" />}
              <span className="absolute top-2 left-2 bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">Deal</span>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <h3 className="font-bold text-slate-900 dark:text-white">{c.name}</h3>
                <span className="font-extrabold text-slate-900 dark:text-white">{gbp(c.price_incl_vat)}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">{(c.components || []).map((x) => `${x.quantity}× ${nameFor(x.product_id)}`).join(" + ")}</p>
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> {scheduleSummary(c)}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {c.happy_hour_enabled && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">Happy Hour −{c.happy_hour_discount_percent}%</span>}
                {c.ab_enabled && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">A/B Test</span>}
                {(c.below_cost || c.below_cost_b) && <span data-testid={`combo-below-cost-${c.id}`} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Below cost</span>}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Badge variant={c.active ? "default" : "secondary"}>{c.active ? "Active" : "Inactive"}</Badge>
                <button data-testid={`combo-timeline-${c.id}`} onClick={() => openTimeline(c)} className="ml-auto text-slate-400 hover:text-blue-600 p-1.5" title="Price timeline"><LineIcon className="h-4 w-4" /></button>
                <button data-testid={`combo-edit-${c.id}`} onClick={() => openEdit(c)} className="text-slate-400 hover:text-blue-600 p-1.5"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`combo-archive-${c.id}`} onClick={() => archive(c)} className="text-slate-400 hover:text-red-600 p-1.5"><Archive className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {!combos.filter((c) => !c.archived).length && <p className="text-slate-400">No deals yet. Create your first meal deal.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit deal" : "New meal deal"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="combo-form-name" value={form.name} onChange={(e) => upd("name", e.target.value)} className="mt-1" /></div>
            <div><Label>Description</Label><Textarea data-testid="combo-form-description" value={form.description} onChange={(e) => upd("description", e.target.value)} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Deal price incl VAT (£)</Label><Input data-testid="combo-form-price" type="number" step="0.01" value={form.price_incl_vat} onChange={(e) => upd("price_incl_vat", e.target.value)} className="mt-1" /></div>
              <div><Label>VAT rate %</Label><Input data-testid="combo-form-vat" type="number" value={form.vat_rate} onChange={(e) => upd("vat_rate", e.target.value)} className="mt-1" /></div>
            </div>
            <div>
              <Label>Included products</Label>
              <div className="flex gap-2 mt-1">
                <Select value={pick} onValueChange={setPick}>
                  <SelectTrigger data-testid="combo-form-product-select" className="flex-1"><SelectValue placeholder="Select a product" /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" data-testid="combo-form-add-product" onClick={addComponent} variant="outline">Add</Button>
              </div>
              <div className="mt-2 space-y-2">
                {form.components.map((c) => (
                  <div key={c.product_id} className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2">
                    <span className="flex-1 text-sm">{nameFor(c.product_id)}</span>
                    <Input type="number" min="1" value={c.quantity} onChange={(e) => setQty(c.product_id, e.target.value)} className="w-16 h-8" />
                    <button onClick={() => removeComponent(c.product_id)} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                {!form.components.length && <p className="text-xs text-slate-400">No products added yet.</p>}
              </div>
            </div>
            <div>
              <Label>Image URL</Label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="combo-form-image-url" value={form.image_url} onChange={(e) => upd("image_url", e.target.value)} placeholder="Paste URL or upload" />
                <label className="shrink-0">
                  <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="combo-form-image-upload" />
                  <span className="inline-flex items-center h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 cursor-pointer text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  </span>
                </label>
              </div>
              {form.image_url && <img src={form.image_url} alt="" className="mt-2 h-20 w-20 rounded-lg object-cover border border-slate-200" />}
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
              <Label className="text-sm font-semibold">Where does this deal apply?</Label>
              <Select value={form.scope || "all"} onValueChange={(v) => upd("scope", v)}>
                <SelectTrigger data-testid="combo-scope" className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All buildings</SelectItem>
                  <SelectItem value="group">A management group</SelectItem>
                  <SelectItem value="buildings">Specific buildings</SelectItem>
                </SelectContent>
              </Select>
              {form.scope === "group" && (
                <div className="mt-2">
                  <Input data-testid="combo-scope-group" list="combo-groups" value={form.group || ""} onChange={(e) => upd("group", e.target.value)} placeholder="e.g. FORA, GPE" />
                  <datalist id="combo-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
                  <p className="text-xs text-slate-400 mt-1">Applies to every building tagged with this group.</p>
                </div>
              )}
              {form.scope === "buildings" && (
                <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto">
                  {buildings.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" data-testid={`combo-building-${b.id}`} checked={(form.building_ids || []).includes(b.id)} onChange={() => toggleBuilding(b.id)} />
                      {b.name}{b.group ? ` · ${b.group}` : ""}
                    </label>
                  ))}
                  {!buildings.length && <p className="text-xs text-slate-400">No buildings yet.</p>}
                </div>
              )}
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
              <div className="flex items-center gap-2"><Switch data-testid="combo-form-schedule" checked={form.schedule_enabled} onCheckedChange={(v) => upd("schedule_enabled", v)} /><Label>Limit to specific days & times</Label></div>
              {form.schedule_enabled && (
                <div className="mt-3 space-y-3">
                  <div className="flex flex-wrap gap-1.5">
                    {DAYS.map((d, i) => (
                      <button key={i} type="button" data-testid={`combo-day-${i}`} onClick={() => toggleDay(i)} className={`h-9 w-11 rounded-lg text-xs font-semibold transition-colors ${form.days.includes(i) ? "bg-blue-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>{d}</button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>From</Label><Input data-testid="combo-start-time" type="time" value={form.start_time} onChange={(e) => upd("start_time", e.target.value)} className="mt-1" /></div>
                    <div><Label>To</Label><Input data-testid="combo-end-time" type="time" value={form.end_time} onChange={(e) => upd("end_time", e.target.value)} className="mt-1" /></div>
                  </div>
                  <p className="text-xs text-slate-400">Leave days empty to run every day within the time window.</p>
                </div>
              )}
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
              <div className="flex items-center gap-2"><Switch data-testid="combo-form-happy-hour" checked={form.happy_hour_enabled} onCheckedChange={(v) => upd("happy_hour_enabled", v)} /><Label>Happy hour auto-discount</Label></div>
              {form.happy_hour_enabled && (
                <div className="mt-3 space-y-3">
                  <div><Label>Discount %</Label><Input data-testid="combo-hh-discount" type="number" value={form.happy_hour_discount_percent} onChange={(e) => upd("happy_hour_discount_percent", e.target.value)} className="mt-1 w-28" /></div>
                  <div className="flex flex-wrap gap-1.5">
                    {DAYS.map((d, i) => (
                      <button key={i} type="button" data-testid={`combo-hh-day-${i}`} onClick={() => toggleHHDay(i)} className={`h-9 w-11 rounded-lg text-xs font-semibold transition-colors ${form.happy_hour_days.includes(i) ? "bg-rose-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>{d}</button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>From</Label><Input data-testid="combo-hh-start" type="time" value={form.happy_hour_start} onChange={(e) => upd("happy_hour_start", e.target.value)} className="mt-1" /></div>
                    <div><Label>To</Label><Input data-testid="combo-hh-end" type="time" value={form.happy_hour_end} onChange={(e) => upd("happy_hour_end", e.target.value)} className="mt-1" /></div>
                  </div>
                  <p className="text-xs text-slate-400">During this window the deal price auto-drops by the discount. Empty days = every day.</p>
                  {belowCostA && (
                    <div data-testid="combo-guardrail-warning" className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 p-2.5 text-sm text-red-700 dark:text-red-300">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>Happy-hour price {gbp(hhPriceA)} is below cost {gbp(formCost)} — this deal would lose money.</span>
                    </div>
                  )}
                  {belowCostB && (
                    <div data-testid="combo-guardrail-warning-b" className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 p-2.5 text-sm text-red-700 dark:text-red-300">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>Variant B happy-hour price {gbp(hhPriceB)} is below cost {gbp(formCost)}.</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
              <div className="flex items-center gap-2"><Switch data-testid="combo-form-ab" checked={form.ab_enabled} onCheckedChange={(v) => upd("ab_enabled", v)} /><Label>A/B test a second version</Label></div>
              {form.ab_enabled && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="col-span-2 text-xs text-slate-400">Variant A is the deal above. Variant B is shown to half of customers.</div>
                  <div><Label>Variant B name</Label><Input data-testid="combo-ab-name" value={form.variant_b?.name || ""} onChange={(e) => upd("variant_b", { ...form.variant_b, name: e.target.value })} placeholder="Optional" className="mt-1" /></div>
                  <div><Label>Variant B price (£)</Label><Input data-testid="combo-ab-price" type="number" step="0.01" value={form.variant_b?.price_incl_vat || 0} onChange={(e) => upd("variant_b", { ...form.variant_b, price_incl_vat: e.target.value })} className="mt-1" /></div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2"><Switch data-testid="combo-form-active" checked={form.active} onCheckedChange={(v) => upd("active", v)} /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="combo-form-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!timeline} onOpenChange={(o) => !o && setTimeline(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Price timeline · {timeline?.combo?.name}</DialogTitle></DialogHeader>
          {timeline && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((d, i) => (
                  <button key={i} data-testid={`timeline-day-${i}`} onClick={() => openTimeline(timeline.combo, i)} className={`h-8 px-3 rounded-lg text-xs font-semibold ${timeline.weekday === i ? "bg-blue-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>{d}</button>
                ))}
              </div>
              <p className="text-xs text-slate-400">Cost per deal: {gbp(timeline.cost)}. Grey = not available, rose = happy-hour price.</p>
              <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5" data-testid="timeline-grid">
                {timeline.timeline.map((row) => {
                  const belowCost = row.available && row.price < timeline.cost;
                  return (
                    <div key={row.hour} className={`rounded-lg p-2 text-center border ${!row.available ? "bg-slate-100 dark:bg-slate-800 text-slate-400 border-transparent" : row.happy_hour ? "bg-rose-50 dark:bg-rose-950/40 border-rose-300 text-rose-700 dark:text-rose-300" : "bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}>
                      <div className="text-[10px] font-medium">{row.hour}</div>
                      <div className="text-xs font-bold">{row.available ? gbp(row.price) : "—"}</div>
                      {belowCost && <div className="text-[9px] font-bold text-red-600">loss</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTimeline(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
